import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import DOMPurify from "dompurify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import { Edit, Delete, Send } from "@mui/icons-material";
import { GoalDetailSkeleton } from "../components/ui/PageContentSkeleton";
import {
  GOAL_STATUS_OPTIONS,
  goalStatusColor,
  goalStatusLabel,
  normalizeGoalStatus,
} from "../utils/goals";
import ActivityLogListItem from "../components/activity/ActivityLogListItem";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import { hasNavWrite, isPrivilegedNavRole } from "../utils/navPermissions";

export default function GoalDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editMode = searchParams.get("edit") === "1";
  const { user } = useAuth();
  const [goal, setGoal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [sendingComment, setSendingComment] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", status: "not_started", due_date: "" });
  const [goalActivity, setGoalActivity] = useState([]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const role = (user?.role || "EMPLOYEE").toUpperCase();
  const isAdmin = role === "ADMIN" || role === "HR";
  const isManager = role === "MANAGER";
  const goalEmployeeId = goal?.employee_id;
  const canMutateGoals =
    isPrivilegedNavRole(user?.role) || hasNavWrite(user, "/goals");

  useEffect(() => {
    setGoal(null);
    setLoading(true);
    setGoalActivity([]);
  }, [id]);

  const fetchGoal = useCallback(async () => {
    try {
      const res = await api.get(`/goals/${id}`);
      const g = res.data?.data || res.data;
      const normalizedStatus = normalizeGoalStatus(g.status);
      setGoal(g);
      setForm({ title: g.title || "", description: g.description || "", status: normalizedStatus, due_date: g.due_date ? g.due_date.slice(0, 10) : "" });
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to load goal");
      navigate("/goals");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchGoalActivity = useCallback(async () => {
    if (!id) return;
    try {
      if (isAdmin || isManager) {
        // Manager / Admin / HR: team activity for the goal owner, filtered to this goal.
        // Backend allows employeeId = own user id (own goals were previously 403 for managers).
        if (!goalEmployeeId) return;
        const res = await api.get("/activity-logs/team", {
          params: {
            employeeId: String(goalEmployeeId),
            entityType: "goal",
            entityId: String(id),
            limit: 50,
          },
        });
        const raw = res.data?.data !== undefined ? res.data.data : res.data;
        setGoalActivity(Array.isArray(raw) ? raw : []);
      } else {
        // Employee: own actions + actions where you are the goal owner (e.g. manager edits your goal).
        const res = await api.get("/activity-logs/me", {
          params: { entityType: "goal", entityId: String(id), limit: 50 },
        });
        const raw = res.data?.data !== undefined ? res.data.data : res.data;
        setGoalActivity(Array.isArray(raw) ? raw : []);
      }
    } catch (_) {
      setGoalActivity([]);
    }
  }, [id, isAdmin, isManager, goalEmployeeId]);

  useEffect(() => {
    fetchGoal();
  }, [fetchGoal]);

  useEffect(() => {
    fetchGoalActivity();
  }, [fetchGoalActivity]);

  useEffect(() => {
    if (!loading && goal && editMode && !canMutateGoals) {
      navigate(`/goals/${id}`, { replace: true });
      toast.info("You have read-only access to Goals.");
    }
  }, [loading, goal, editMode, canMutateGoals, navigate, id]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (!form.title.trim()) {
        toast.error("Goal is required");
        return;
      }
      await api.put(`/goals/${id}`, form);
      toast.success("Goal updated");
      setGoal((g) => ({ ...g, ...form }));
      fetchGoalActivity();
      navigate(`/goals/${id}`, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update goal");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/goals/${id}`);
      toast.success("Goal deleted");
      setDeleteOpen(false);
      navigate("/goals");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete goal");
    } finally {
      setDeleting(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    setSendingComment(true);
    try {
      const res = await api.post(`/goals/${id}/comments`, { text: commentText.trim() });
      setGoal((g) => ({ ...g, comments: res.data?.data?.comments || res.data?.comments || g.comments || [] }));
      setCommentText("");
      fetchGoalActivity();
      toast.success("Comment added");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to add comment");
    } finally {
      setSendingComment(false);
    }
  };

  if (loading || !goal) return <GoalDetailSkeleton />;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-2">
        <button onClick={() => navigate("/goals")} className="text-primary hover:underline">← Back to Goals</button>
        <div className="flex flex-wrap gap-2 justify-end">
          {!editMode ? (
            <>
              {canMutateGoals && (
                <>
                  <button onClick={() => navigate(`/goals/${id}?edit=1`)} className="btn-primary flex items-center gap-1 px-3 py-1.5 text-sm"><Edit className="w-4 h-4" /> Edit</button>
                  <button type="button" onClick={() => setDeleteOpen(true)} className="px-3 py-1.5 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50 flex items-center gap-1"><Delete className="w-4 h-4" /> Delete</button>
                </>
              )}
            </>
          ) : (
            <button onClick={() => navigate(`/goals/${id}`)} className="px-3 py-1.5 text-sm border rounded-lg">Cancel</button>
          )}
        </div>
      </div>

      {editMode && canMutateGoals ? (
        <form onSubmit={handleSave} className="bg-white rounded-xl shadow p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Goal *</label>
            <input type="text" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Key Results / Measure of Success</label>
            <div className="rounded-lg border border-gray-200 overflow-hidden focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary">
              <ReactQuill
                theme="snow"
                value={form.description}
                onChange={(value) => setForm((f) => ({ ...f, description: value }))}
                placeholder="Describe measurable outcomes, milestones, and success criteria..."
                modules={{
                  toolbar: [
                    ["bold", "italic", "underline"],
                    [{ list: "ordered" }, { list: "bullet" }],
                    ["link"],
                    ["clean"],
                  ],
                }}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary">
              {GOAL_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Due date</label>
            <input type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
          </div>
          <button type="submit" disabled={saving} className="btn-primary px-4 py-2">Save</button>
        </form>
      ) : (
        <div className="bg-white rounded-xl shadow p-6 mb-6">
          <h1 className="text-2xl font-bold mb-2">{goal.title}</h1>
          <div
            className="text-gray-600 text-sm leading-relaxed break-words overflow-x-hidden [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-1 [&_p]:mb-2 [&_a]:text-primary [&_a]:underline [&_strong]:font-semibold"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(goal.description || "<p>—</p>"),
            }}
          />
          <div className="mt-2 flex gap-2 items-center">
            <span className={"px-2 py-0.5 rounded text-sm font-medium " + goalStatusColor(goal.status)}>{goalStatusLabel(goal.status)}</span>
            {goal.due_date && <span className="text-sm text-gray-500">Due: {goal.due_date}</span>}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow p-6">
        <h2 className="text-lg font-semibold mb-3">Comments</h2>
        {canMutateGoals ? (
          <form onSubmit={handleAddComment} className="flex flex-col sm:flex-row gap-2 mb-4">
            <input
              type="text"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              className="w-full sm:flex-1 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              placeholder="Add a comment..."
            />
            <button
              type="submit"
              disabled={sendingComment || !commentText.trim()}
              className="btn-primary flex items-center gap-1 px-4 py-2 w-full sm:w-auto justify-center"
            >
              <Send className="w-4 h-4" /> Send
            </button>
          </form>
        ) : (
          <p className="text-sm text-gray-500 mb-4">Comments are read-only for your account.</p>
        )}
        <ul className="space-y-2">
          {(goal.comments || []).map((c) => (
            <li key={c.id} className="border-l-2 border-gray-200 pl-3 py-1">
              <span className="font-medium text-sm">{c.userName || "User"}</span>
              <span className="text-xs text-gray-400 ml-2">{c.createdAt ? new Date(c.createdAt).toLocaleString() : ""}</span>
              <p className="text-gray-700 mt-0.5">{c.text}</p>
            </li>
          ))}
          {(!goal.comments || goal.comments.length === 0) && <p className="text-gray-500 text-sm">No comments yet.</p>}
        </ul>
      </div>

      <div className="bg-white rounded-xl shadow p-6 mt-6">
        <h2 className="text-lg font-semibold mb-3">Activity Log</h2>
        <ul className="space-y-2">
          {goalActivity.map((entry) => (
            <ActivityLogListItem key={entry.id} entry={entry} />
          ))}
          {goalActivity.length === 0 && <p className="text-gray-500 text-sm">No activity for this goal yet.</p>}
        </ul>
      </div>

      <ConfirmDialog
        open={deleteOpen}
        title="Delete goal"
        message={`Delete “${goal.title}”? This cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        danger
        loading={deleting}
        onCancel={() => !deleting && setDeleteOpen(false)}
        onConfirm={handleDelete}
      />
    </div>
  );
}
